import { useContext, useEffect, useState } from 'react';
import { LoginContext } from '../../contexts/LoginContext';
import { currentDateForFile, firstDayMonth, todayDate, todayLastYear, YMD_FORMAT } from '../../utils/DateUtils';
import { AppContext } from '../../contexts/AppContext';
import moment from 'moment';
import LogisticsAPI from '../../services/LogisticsAPI';
import { decodeFileXLSX } from '../../utils/MiscellaneousUtils';

export const SORT = {
  UPWARD: 'upward',
  DOWNWARD: 'downward',
  NONE: '',
};

const useLogisticsPresenter = tableRef => {
  const { isSupplier, idSupplier } = useContext(LoginContext);
  const { showError, showWarning, showSuccess } = useContext(AppContext);

  const [firstLoad, setFirstLoad] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [dateFrom, setDateFrom] = useState(isSupplier ? firstDayMonth() : null);
  const [dateTo, setDateTo] = useState(isSupplier ? moment() : null);
  const [client, setClient] = useState('');
  const [reference, setReference] = useState('');
  const [rows, setRows] = useState([]);
  const [rowsToShow, setRowsToShow] = useState([]);
  const [rowsToShowSorted, setRowsToShowSorted] = useState([]);
  const [sort, setSort] = useState('');

  const [oldSearchRequest, setOldSearchRequest] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);

  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingApproval, setLoadingApproval] = useState(false);

  const [showInvoicedApproved, setShowInvoicedApproved] = useState(false);
  const [showNotInvoicedApproved, setShowNotInvoicedApproved] = useState(true);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    isSupplier && search();
  }, []);

  const subFilterRows = rows => {
    if (showInvoicedApproved && showNotInvoicedApproved) {
      return rows;
    } else {
      return isSupplier
        ? rows.filter(r => r.supplierInvoiced == showInvoicedApproved)
        : rows.filter(r => r.acerBragApproved == showInvoicedApproved);
    }
  };

  useEffect(() => {
    setRowsToShow(subFilterRows(rows));
    setRowsToShowSorted(subFilterRows(rows));
  }, [showInvoicedApproved, showNotInvoicedApproved, rows]);

  const cleanFilters = () => {
    setSelectedSupplier(null);
    setDateFrom(null);
    setDateTo(null);
    setClient('');
    setReference('');
  };

  const search = reload => {
    const request = reload
      ? oldSearchRequest
      : {
          cuit: isSupplier ? idSupplier : selectedSupplier.cuit,
          startDate: dateFrom ? dateFrom.format(YMD_FORMAT) : undefined,
          endDate: dateTo ? dateTo.format(YMD_FORMAT) : undefined,
          client: !client.isBlank() ? client : undefined,
          reference: !reference.isBlank() ? reference : undefined,
        };

    setOldSearchRequest(request);

    firstLoad && setFirstLoad(false);
    setLoading(true);
    setSelectedRows([]);

    LogisticsAPI.getLogisticGrid(request)
      .then(res => setRows(res.data))
      .catch(error => showError('Error al obtener los registros'))
      .finally(() => setLoading(false));
  };

  const reloadGrid = () => search(true);

  const updateGrid = (reference, hasComment) => {
    const updatedRows = rows.clone();
    const rowIndex = updatedRows.findIndex(r => r.reference === reference);

    updatedRows[rowIndex].hasAcerBragComments = hasComment;

    setRows(updatedRows);
  };

  const validateFilters = () => {
    let errors = [];
    const errorMessages = [];
    let i = 0;

    if ((errors[i++] = !isSupplier && !selectedSupplier)) {
      errorMessages.push('Debe seleccionar un proveedor para iniciar la búsqueda');
    }

    if ((errors[i++] = dateFrom && !dateTo)) {
      errorMessages.push('Debe completar el rango de fechas o quitar la Fecha desde');
    }

    if ((errors[i++] = !dateFrom && dateTo)) {
      errorMessages.push('Debe completar el rango de fechas o quitar la Fecha hasta');
    }

    const hasErrors = errors.some(e => e);

    hasErrors && showWarning(errorMessages);

    return !hasErrors;
  };

  const validateAndSearch = () => validateFilters() && search();

  const handleExport = references => {
    setLoadingExport(true);

    const request = {
      ...oldSearchRequest,
      reference: references?.join(',') || undefined,
      pageNumber: 0,
      pageSize: 0,
    };

    return LogisticsAPI.exportToExcel(request)
      .then(res => decodeFileXLSX(res, `Logistica_${currentDateForFile()}.xlsx`))
      .catch(error => showError('No se pudo descargar el archivo solicitado'))
      .finally(() => setLoadingExport(false));
  };

  const handleApproval = () => {
    setLoadingApproval(true);

    const request = {
      references: selectedRows.map(r => r.reference),
      cuit: isSupplier ? idSupplier : oldSearchRequest.cuit,
      approved: true,
    };

    LogisticsAPI.approveReferences(request)
      .then(res => {
        showSuccess('Los registros se han aprobado con éxito');
        setSelectedRows([]);
        reloadGrid();
        tableRef.current.onAllSelected(false);
      })
      .catch(error => showError('Error al aprobar los registros seleccionados'))
      .finally(() => setLoadingApproval(false));
  };

  const sortByOrderNumber = (a, b, sort) => {
    const condition = sort === SORT.UPWARD;
    const elem = condition ? a : b;
    const comparison = condition ? b : a;

    return elem.reference.localeCompare(comparison.reference, 'en', { numeric: true });
  };

  return {
    selectedSupplier,
    setSelectedSupplier,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    client,
    setClient,
    reference,
    setReference,
    validateAndSearch,
    cleanFilters,
    rows: sort !== SORT.NONE ? rowsToShowSorted.sort((a, b) => sortByOrderNumber(a, b, sort)) : rowsToShow,
    firstLoad,
    loading,
    gridFunctions: {
      disableSelectAll: showInvoicedApproved && !showNotInvoicedApproved,
      reloadGrid,
      updateGrid,
      selectedRows,
      setSelectedRows,
      open,
      setOpen,
      cuit: oldSearchRequest.cuit,
      gridActions: {
        selectedSupplier,
        handleExport,
        loadingExport,
        handleApproval,
        loadingApproval,
        sort,
        setSort,
        showInvoicedApproved,
        setShowInvoicedApproved,
        showNotInvoicedApproved,
        setShowNotInvoicedApproved,
      },
    },
  };
};

export default useLogisticsPresenter;
